import React from 'react'
import { Chip, Stack, Typography, Box } from '@mui/material'
import type { SoldVehicle } from '../types'
import { formatPrice } from '../format'
import { extractVehiclePrice } from '../hooks/useJusteEnchereGame'
import { goldFrameStyles, TV_COLORS } from '../designSystem'

type VehicleSnapshotProps = {
  vehicle: SoldVehicle | null
  revealPrice?: boolean
}

const numberFormatter = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 })

const VehicleSnapshot = ({ vehicle, revealPrice = false }: VehicleSnapshotProps) => {
  if (!vehicle) {
    return (
      <Typography
        variant="body2"
        color="text.secondary"
      >
        On prépare le prochain véhicule...
      </Typography>
    )
  }

  const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

  const make = (vehicle.make ?? '').trim()
  const base = (vehicle.normalVersion || vehicle.model || vehicle.version || '').trim()
  const hasMake = make && new RegExp(`\\b${escapeRegExp(make)}\\b`, 'i').test(base)
  const title = [hasMake ? '' : make, base].filter(Boolean).join(' ')
  const mileage = typeof vehicle.mileage === 'number' ? `${numberFormatter.format(vehicle.mileage)} km` : 'N/C'
  const age = typeof vehicle.age === 'number' ? `${Math.round(vehicle.age / 365)} ans` : 'N/C'
  const country = vehicle.auctionCountryCode ?? '??'
  const nonOp = vehicle.nonOp ?? false
  const revealedValue = extractVehiclePrice(vehicle)

  const lotLabel = vehicle.erpId ?? vehicle.id

  return (
    <Stack spacing={0.8} sx={{ mb: 1.5 }}>
      <Box
        sx={{
          ...goldFrameStyles,
          borderRadius: 2,
          p: 1.5,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 10,
            right: -20,
            transform: 'rotate(35deg)',
            bgcolor: TV_COLORS.red,
            color: '#fff',
            pr: 4,
            pl: 6,
            py: 0.3,
            fontFamily: 'Bebas Neue',
            fontSize: 18,
            textTransform: 'uppercase',
            boxShadow: '0 6px 12px rgba(0,0,0,0.45)',
          }}
        >
          À estimer
        </Box>
        <Typography
          variant="overline"
          sx={{ fontFamily: 'Orbitron', letterSpacing: 2, color: TV_COLORS.yellow }}
        >
          Lot n° {lotLabel}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            fontFamily: 'Bebas Neue',
            lineHeight: 1.1,
            textTransform: 'uppercase',
            color: '#fff',
            minHeight: '2.2em',
          }}
        >
          {title || 'Véhicule mystère'}
        </Typography>
      </Box>
      <Stack
        direction="row"
        spacing={0.7}
        flexWrap="wrap"
        sx={{ gap: 0.5 }}
      >
        <Chip
          size="small"
          label={`Kilométrage : ${mileage}`}
          sx={{
            px: 0.9,
            py: 0.15,
            bgcolor: 'rgba(0,0,0,0.35)',
            color: '#fff',
            fontSize: 11,
            border: '1px solid rgba(249,200,70,0.6)',
          }}
        />
        <Chip
          size="small"
          label={`Âge : ${age}`}
          sx={{
            px: 0.9,
            py: 0.15,
            bgcolor: 'rgba(0,0,0,0.35)',
            color: '#fff',
            fontSize: 11,
            border: '1px solid rgba(249,200,70,0.6)',
          }}
        />
        <Chip
          size="small"
          label={`Pays : ${country}`}
          sx={{
            px: 0.9,
            py: 0.15,
            bgcolor: 'rgba(0,0,0,0.35)',
            color: '#fff',
            fontSize: 11,
            border: '1px solid rgba(249,200,70,0.6)',
          }}
        />

        {nonOp ? (
          <Chip
            size="small"
            label="Non roulant"
            sx={{
              px: 0.9,
              py: 0.15,
              bgcolor: 'rgba(229,57,53,0.6)',
              color: '#fff',
              fontSize: 11,
              border: '1px solid rgba(249,200,70,0.6)',
            }}
          />
        ) : null}
        {vehicle.fuelType ? (
          <Chip
            size="small"
            label={vehicle.fuelType}
            sx={{
              px: 0.9,
              py: 0.15,
              bgcolor: 'rgba(0,0,0,0.35)',
              color: '#fff',
              fontSize: 11,
              border: '1px solid rgba(249,200,70,0.6)',
            }}
          />
        ) : null}
      </Stack>
      {revealPrice ? (
        <Typography variant="body2" color={TV_COLORS.yellow} sx={{ fontFamily: 'Orbitron' }}>
          Prix adjugé : {formatPrice(revealedValue)}
        </Typography>
      ) : null}
    </Stack>
  )
}

export default VehicleSnapshot
